import { Client, Server, Ticket, Announcement, AccountingItem } from "../types";

const API_BASE_URL = window.location.origin;

// Helper function to manage requests and errors
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  try {
    const user = localStorage.getItem('lisa_server_auth_user');
    const headers = new Headers(options.headers || {});
    
    // AÑADIMOS EL TOKEN A TODAS LAS PETICIONES
    if (user) {
        const { token } = JSON.parse(user);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
    }
    // Aseguramos el Content-Type si hay un body
    if (options.body) {
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Ocurrió un error en el servidor.');
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }
    return response.json();
  } catch (error: any) {
    console.error(`Error en la petición a ${endpoint}:`, error);
    throw error;
  }
}

export const api = {
    // --- Clients ---
    getClients: (): Promise<Client[]> => request('/api/data/clients'),
    addClient: (client: Omit<Client, "id" | "hash" | "createdAt">): Promise<Client> => request('/api/data/clients', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(client),
    }),
    deleteClient: (clientId: string): Promise<any> => request(`/api/data/clients/delete/${clientId}`, { method: "POST" }),

    // Function to update client status
    updateClientStatus: (clientId: string, status: 'active' | 'banned'): Promise<any> => request(`/api/clients/${clientId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    }),
    // FUNCION NOTIFICACION TELEGRAM
saveClientTelegramId: (clientId: string, chatId: string): Promise<any> => request(`/api/clients/${clientId}/telegram`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ chatId }),
}),

// --- Tráfico de Servidor ---
    getServerTraffic: (params: { ip: string; unit: 'Mbps' | 'Pps'; time: number }): Promise<any> => {
        const { ip, unit, time } = params;
        // La URL no necesita serverId
        return request(`/api/servers/traffic?ip=${ip}&unit=${unit}&time=${time}`);
    },

    // ESTA ES LA NUEVA FUNCIÓN PARA EL CLIENTE (SIN TOKEN)
getClientServerTraffic: (params: { serverId: string, ip: string, unit: string, time: number, clientId: string }): Promise<any> => {
    // Ya no necesitamos el token
    const queryString = `?serverId=${params.serverId}&ip=${params.ip}&unit=${params.unit}&time=${params.time}&clientId=${params.clientId}`;
    return request(`/api/client/traffic${queryString}`);
},

// ESTA ES LA NUEVA FUNCIÓN PARA EL ANCHO DE BANDA (V1)
getClientBandwidth: (params: { serverId: string, royaleServiceId: string, time: number, clientId: string }): Promise<any> => {
    // 'unit' ha sido eliminado de los parámetros y del queryString
     const queryString = `?serverId=${params.serverId}&royaleServiceId=${params.royaleServiceId}&time=${params.time}&clientId=${params.clientId}`;
     return request(`/api/client/bandwidth${queryString}`);
},

    // --- Servers ---
    getServers: (): Promise<Server[]> => request('/api/data/servers'),
    getClientServers: (): Promise<Server[]> => request('/api/client/servers'),
    getServersForClient: (clientId: string): Promise<Server[]> => request(`/api/data/client-servers/${clientId}`),
    addServer: (server: Omit<Server, "id" | "status">): Promise<Server> => request('/api/data/servers', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(server),
    }),
    deleteServer: (serverId: string): Promise<any> => request(`/api/data/servers/delete/${serverId}`, { method: "POST" }),
    updateServer: (serverId: string, serverData: Partial<Server>): Promise<Server> => request(`/api/data/servers/update/${serverId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(serverData),
    }),
    assignServer: (serverId: string, clientIds: string[]): Promise<any> => request(`/api/data/servers/${serverId}/sync-assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientIds }),
    }),

    // --- Actions ---
    executeServerAction: (serverId: string, action: 'start' | 'stop' | 'restart', logData?: { reason: string, clientId: string, clientName: string }): Promise<any> => request(`/api/actions/${serverId}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(logData),
    }),
    toggleServerLock: (serverId: string): Promise<any> => request(`/api/actions/${serverId}/toggle-lock`, { method: "POST" }),
    renewServer: (serverId: string): Promise<any> => request(`/api/actions/${serverId}/renew`, { method: "POST" }),

    // --- Stripe ---
    createCheckoutSession: (data: { serverId: string; clientId: string; priceId: string; }): Promise<{ url: string }> => request('/api/stripe/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

    // --- Tickets ---
    getTickets: (): Promise<Ticket[]> => request('/api/data/tickets'),
    getClientTickets: (clientId: string): Promise<Ticket[]> => request(`/api/data/tickets/by-client/${clientId}`),
    createTicket: (ticketData: { clientId: string; subject: string; message: string; priority: string; }): Promise<Ticket> => request('/api/data/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData),
    }),
    getTicketDetails: (ticketId: string): Promise<Ticket & { replies: any[] }> => request(`/api/data/tickets/${ticketId}`),
    addTicketReply: (ticketId: string, replyData: { authorId: string; authorRole: 'admin' | 'client'; message: string; }) => request(`/api/data/tickets/${ticketId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(replyData),
    }),
    updateTicketStatus: (ticketId: string, status: string): Promise<any> => request(`/api/data/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    }),

    // --- Announcements ---
    getAnnouncements: (): Promise<Announcement[]> => request('/api/data/announcements'),
    getAllAnnouncements: (): Promise<Announcement[]> => request('/api/data/announcements/all'),
    createAnnouncement: (data: { message: string; imageUrl?: string }): Promise<any> => request('/api/data/announcements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    }),
    deleteAnnouncement: (id: number): Promise<any> => request(`/api/data/announcements/${id}`, { method: 'DELETE' }),

    // =================================================================
    //   BLOQUE A AÑADIR - Lógica para Ciclos Contables
    // =================================================================
    getAccountingCycles: (): Promise<any[]> => request('/api/accounting/cycles'),
    getAllTransactions: () => request('/api/accounting/transactions'),
    getAllExpenses: () => request('/api/accounting/expenses'),

    
    previewMercuryDistribution: (cycleId: number): Promise<any> => request(`/api/accounting/cycles/${cycleId}/distributions/preview-mercury`, {
        method: 'POST',
    }),
   startNewAccountingCycle: (): Promise<{ message: string; newCycleId: number }> => request('/api/accounting/cycles/start', {
        method: 'POST',
    }),

    // --- Accounting ---
    getAccountingSummary: (): Promise<{ monthlyIncome: number; monthlyExpenses: number; netProfit: number; totalBalance: number; }> => request('/api/accounting/summary'),
    
    // ================== CAMBIO AQUÍ ==================
    getExpenses: (): Promise<AccountingItem[]> => {
        return request('/api/accounting/expenses').then(data => {
            console.log("✅ DATOS DE GASTOS RECIBIDOS:", data); // Mostramos los datos en consola
            return data; // Devolvemos los datos para que la app siga funcionando
        });
    },
    // ================== Y CAMBIO AQUÍ ==================
    getTransactions: (): Promise<AccountingItem[]> => {
        return request('/api/accounting/transactions').then(data => {
            console.log("✅ DATOS DE INGRESOS (TRANSACCIONES) RECIBIDOS:", data); // Mostramos los datos en consola
            return data; // Devolvemos los datos para que la app siga funcionando
        });
    },

   closeAccountingCycle: (cycleId: number, data: { distributions: any; retained_fund: number }): Promise<any> => {
    return request(`/api/accounting/cycles/${cycleId}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
},   

    // ================== FIN DE LOS CAMBIOS ==================

    addExpense: (expenseData: { expenseDate: string; amount: number; vendor: string; description?: string }): Promise<any> => request('/api/accounting/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expenseData),
    }),
    addTransaction: (transactionData: { transactionDate: string; amount: number; clientId: string; description?: string }): Promise<any> => request('/api/accounting/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionData),
    }),
    syncRoyaleInvoices: (): Promise<{ newInvoices: number }> => request('/api/accounting/sync-royale-invoices', {
        method: 'POST',
    }),
    syncMercury: (daysBack?: number): Promise<any> => request('/api/accounting/sync-mercury', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daysBack: daysBack || 30 }),
    }),

    deleteMovement: async (type: 'income' | 'expense', id: string | number): Promise<any> => {
        const response = await fetch(`/api/accounting/movement/delete/${type}/${id}`, {
            method: 'POST',
        });
        if (!response.ok) {
            throw new Error('Error deleting movement');
        }
        return response.json();
    },

    updateMovement: (type: 'income' | 'expense', id: string | number, data: AccountingItem): Promise<any> => {
        const payload = {
            ...data,
            expenseDate: type === 'expense' ? data.date.toISOString().split('T')[0] : undefined,
            transactionDate: type === 'income' ? data.date.toISOString().split('T')[0] : undefined,
        };
        return request(`/api/accounting/movement/${type}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
    },

    // --- Logs ---
    getLogs: (): Promise<any[]> => request('/api/data/logs'),
    logLoginSuccess: (logData: { clientId: string; clientName: string }): Promise<any> => request('/api/logs/login-success', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData),
    }),
};
